import React from 'react';
import { Box, Button, Flex, IconButton, Image, Tooltip } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiLogOut } from 'react-icons/fi';
import ADMIN_NAV_ITEMS from './nav';
import AdminNavLink from './AdminNavLink';
import logoSimple from '../../../img/logoSimple.png';
import logo1 from '../../../img/logo1.png';
import logoutAndRedirect from '../../lib/adminLogout';
import { expandHitArea } from '../../../theme/foundations/adminPanel';

/**
 * Sidebar del panel administrativo: soporta modo expandido (208px con logoSimple)
 * y modo colapsado (68px con logo1 e iconos centrados con Tooltip).
 */
export default function AdminSidebar({ isCollapsed = false, onToggleCollapse }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logoutAndRedirect(navigate);
  };

  const currentWidth = isCollapsed ? '68px' : '208px';

  return (
    <Box
      as="aside"
      display={{ base: 'none', lg: 'flex' }}
      flexDirection="column"
      position="fixed"
      top={0}
      left={0}
      w={currentWidth}
      h="100dvh"
      bg="adminPanel.navy"
      color="white"
      overflow="visible"
      zIndex="docked"
      transition="width 0.2s ease-in-out"
    >
      {/* Botón flotante de alternar minimizado en el borde exterior del sidebar */}
      {onToggleCollapse && (
        <IconButton
          icon={isCollapsed ? <FiChevronRight size={14} /> : <FiChevronLeft size={14} />}
          aria-label={isCollapsed ? 'Expandir menú lateral' : 'Minimizar menú lateral'}
          onClick={onToggleCollapse}
          position="absolute"
          top="24px"
          right="-13px"
          boxSize="26px"
          minW="26px"
          h="26px"
          borderRadius="full"
          bg="white"
          color="adminPanel.navy"
          border="1px solid"
          borderColor="adminPanel.cardBorder"
          boxShadow="md"
          zIndex={15}
          _hover={{ bg: 'adminPanel.bg', color: 'adminPanel.navyHover', transform: 'scale(1.08)' }}
          transition="all 0.15s ease-in-out"
        />
      )}

      <Flex
        align="center"
        justify="center"
        h="76px"
        px={isCollapsed ? '8px' : '16px'}
        py="16px"
        flexShrink={0}
      >
        {isCollapsed ? (
          <Image
            src={logo1}
            alt="SIESA"
            boxSize="36px"
            objectFit="contain"
          />
        ) : (
          <Image
            src={logoSimple}
            alt="SIESA"
            w="140px"
            maxH="38px"
            objectFit="contain"
          />
        )}
      </Flex>

      <Box
        as="nav"
        aria-label="Navegación administrativa"
        overflowY="auto"
        overflowX="hidden"
        py="4px"
      >
        <Flex direction="column" gap="10px">
          {ADMIN_NAV_ITEMS.map(item => (
            <AdminNavLink
              key={item.href}
              item={item}
              isActive={pathname === item.href}
              isCollapsed={isCollapsed}
            />
          ))}
        </Flex>
      </Box>

      <Box
        px={isCollapsed ? '8px' : '13px'}
        pt="12px"
        pb="16px"
        mt="auto"
        borderTop="1px solid"
        borderColor="whiteAlpha.200"
        flexShrink={0}
      >
        {isCollapsed ? (
          <Tooltip label="Cerrar sesión" placement="right" hasArrow>
            <IconButton
              icon={<FiLogOut />}
              aria-label="Cerrar sesión"
              onClick={handleLogout}
              w="full"
              minH="40px"
              h="40px"
              variant="ghost"
              color="white"
              _hover={{ bg: 'adminPanel.navyHover' }}
            />
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            w="full"
            justifyContent="flex-start"
            minH="40px"
            h="40px"
            px="12px"
            borderRadius="7px"
            fontSize="14px"
            color="white"
            leftIcon={<Box as={FiLogOut} aria-hidden="true" boxSize="17px" />}
            onClick={handleLogout}
            _hover={{ bg: 'adminPanel.navyHover' }}
            {...expandHitArea(40)}
          >
            Cerrar sesión
          </Button>
        )}
      </Box>
    </Box>
  );
}
